import { assignDeep } from "@/helpers/utility/CollectionFunctions";
import { charts } from "@editor/items/charts/charts";
import { getAxisConfig } from "@editor/items/charts/chartGetters";
import { splitUid } from "@/helpers/utility/Id";
import { useModelStore } from "@editor/items/model/model.store";
import { useReactiveChartData } from "@editor/items/charts/useChartData";
import { useSimulationStore } from "@editor/items/simulation/simulations.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { VIRTUAL_END_ROUND_INDEX } from "@editor/items/charts/ChartItem";

import type { Uid } from "@/helpers/utility/Id";
import type { ChartAxisOption } from "@editor/items/charts/charts";
import type {
  ChartSettings,
  ChartType,
  ChartItem,
  ItemConfig,
} from "@editor/items/charts/ChartItem";
import type { Subset } from "@/helpers/utility/Type";
import type { UnfilledSymbolName } from "@core/ui/svg-icons/symbols/SymbolName";

/**
 * Update the chart type of a chart, making sure the overall configuration remains valid.
 * @param item evaluation or chart
 * @param newChartType (e.g., "line", "bar", ...)
 */
export function updateChartType(
  item: { chartType: ChartType | null; chartSettings: ChartSettings },
  newChartType: ChartType
): void {
  const isFirstChartType = item.chartType == null;

  item.chartType = newChartType;

  // make sure the axis configuration is still valid,
  // otherwise, update it.
  const chartConfig = charts[item.chartType];
  let newAxisConfig: ChartAxisOption;

  if (isFirstChartType) {
    newAxisConfig = chartConfig.axisOptions[0];

    // Assign data defaults.
    assignDeep(item, newAxisConfig.defaults || {});
  } else {
    const sameAxisConfig = getAxisConfig(
      chartConfig,
      item.chartSettings.xAxis,
      item.chartSettings.yAxis
    );

    // No axis option with the same settings found?
    // Change it!
    if (!sameAxisConfig) {
      // Try keeping the X axis stable.
      const configWithSameX = getAxisConfig(
        chartConfig,
        item.chartSettings.xAxis,
        undefined
      );
      if (configWithSameX) {
        newAxisConfig = configWithSameX;
      } else {
        // ...Otherwise, try keeping the y axis stable.
        const configWithSameY = getAxisConfig(
          chartConfig,
          undefined,
          item.chartSettings.yAxis
        );
        if (configWithSameY) {
          newAxisConfig = configWithSameY;
        } else {
          // if no axes correspond, simply assign the axes of the first available axis option.
          newAxisConfig = chartConfig.axisOptions[0];
        }
      }
    } else {
      newAxisConfig = sameAxisConfig;
    }
  }

  // Update axes:
  item.chartSettings.xAxis = newAxisConfig.x ?? item.chartSettings.xAxis;
  item.chartSettings.yAxis = newAxisConfig.y ?? item.chartSettings.yAxis;
}

/**
 * Update one axis of the chart settings, making sure the overall configuration remains valid.
 * @param item evaluation or chart
 * @param axis "x" or "y"
 * @param newValue new axis value
 */
export function updateAxis<A extends "x" | "y">(
  item: { chartType: ChartType | null; chartSettings: ChartSettings },
  axis: A,
  newValue: A extends "x" ? string : "value" | "effect" | "gain" | "impulseSum" | "impulse"
): void {
  const settings = item.chartSettings;

  if (!item.chartType) {
    throw new Error("This chart has no type.");
  }

  const otherDimension = axis == "x" ? "y" : "x";
  const dimensionAxis = `${axis}Axis` as const;
  const otherAxis = `${otherDimension}Axis` as const;

  const dimensions = {
    xAxis: settings.xAxis,
    yAxis: settings.yAxis
  };
  dimensions[dimensionAxis] = newValue as any;

  // make sure the axis configuration is valid,
  // otherwise, change the other axis too.
  const chartConfig = charts[item.chartType];
  let axisConfig = getAxisConfig(
    chartConfig,
    dimensions.xAxis,
    dimensions.yAxis
  );
  if (!axisConfig) {
    const tempDimensions: {
      xAxis: string | undefined;
      yAxis: string | undefined;
    } = {
      ...dimensions
    };
    tempDimensions[otherAxis] = undefined;
    axisConfig = getAxisConfig(
      chartConfig,
      tempDimensions.xAxis,
      tempDimensions.yAxis
    );

    if (!axisConfig) {
      throw new Error(
        `could not find axis config with x:${tempDimensions.xAxis}, y:${tempDimensions.yAxis}`
      );
    }

    const otherAxisValue = axisConfig[otherDimension];

    if (otherAxisValue != null) {
      dimensions[otherAxis] = otherAxisValue as any;
    }
  }

  settings.xAxis = dimensions.xAxis;
  settings.yAxis = dimensions.yAxis;
}

/**
 * Add items to the chart data and itemConfigs.
 * @param chart evaluation or chart
 * @param entries UIDs of items to add (elements, ...)
 */
export function addItems(
  chart: {
    data: ChartItem["data"];
    itemConfigs: ChartItem["itemConfigs"];
  },
  entries: Uid<"target" | "resource" | "index">[]
) {
  const chartData = chart.data;
  for (const uid of entries) {
    const { type, id } = splitUid(uid);
    chartData[type].push(id);

    chartData.selectedItem = { type, id };

    if (!chart.itemConfigs[type][id]) {
      chart.itemConfigs[type][id] = {
        open: false,
        color: null
      };
    }
  }
}

/**
 * Remove items from the chart data.
 * @param chart evaluation or chart
 * @param itemUids UIDs of items to remove (elements, ...)
 */
export function removeItems(chart: {
  data: ChartItem["data"];
  itemConfigs: ChartItem["itemConfigs"];
  dataToggles: { all: { target: boolean; resource: boolean; index: boolean } };
}, itemUids: Uid<"target" | "resource" | "index">[]) {
  const types = itemUids.reduce((acc, uid) => {
    const { type, id } = splitUid(uid);
    if (!acc[type]) acc[type] = [];
    acc[type].push(id);
    return acc;
  }, {} as Record<"target" | "resource" | "index", number[]>);

  const chartData = chart.data;

  for (const _type in types) {
    const type = _type as "target" | "resource" | "index";

    const store = useModelStore().getStore(type);
    if (store) {
      if (chart.dataToggles.all[type]) {
        chart.dataToggles.all[type] = false;
        chartData[type] = store.order.slice();
      }
    }

    const idsToRemove = types[type];
    const currentIds = chartData[type];

    for (const id of idsToRemove) {
      const index = currentIds.indexOf(id);
      if (index >= 0) {
        currentIds.splice(index, 1);
      }
    }
  }
}

/**
 * Clear all items of a certain type from the chart data.
 * @param chart evaluation or chart
 * @param type "type" of items to clear (targets, resources, indices)
 */
export function clearItemsOfType(
  chart: {
    data: ChartItem["data"];
    itemConfigs: ChartItem["itemConfigs"];
    dataToggles: { all: { target: boolean; resource: boolean; index: boolean } };
  },
  type: "target" | "resource" | "index"
) {
  chart.dataToggles.all[type] = false;
  chart.data[type] = [];
}


/**
 * Add a simulation symbol if the chart does not have one yet.
 */
export function addSimulationSymbol(
  simulationConfigs: Subset<{ [simulationId: number | string]: ItemConfig }>,
  simulationId: number
) {
  let config = simulationConfigs[simulationId];

  // add a simulation icon if there is none
  if (config == null) {
    config = {
      open: false,
      color: null
    };
    simulationConfigs[simulationId] = config;
  }

  // if there is a symbol already, return it.
  const existingSymbol = config.symbol;
  if (existingSymbol != null) {
    return existingSymbol;
  }

  const newSymbol = getLeastUsedSimulationSymbol(simulationConfigs);
  config.symbol = newSymbol;

  return newSymbol;
}


/**
 * Get the least used simulation symbol of a chart.
 */
function getLeastUsedSimulationSymbol(
  simulationConfigs: Subset<{
    [simulationId: number | string]: ItemConfig;
  }>
) {
  const symbolUses = Object.values(simulationConfigs).reduce((sum, config) => {
    if (!config) return sum;
    if (!config.symbol) return sum;
    if (!sum.has(config.symbol)) sum.set(config.symbol, 1);
    else sum.set(config.symbol, (sum.get(config.symbol) || 0) + 1);
    return sum;
  }, new Map<UnfilledSymbolName, number>());
  const availableSymbols: UnfilledSymbolName[] = [
    "circle",
    "square",
    "diamond",
    "clover",
    "drop",
    "hexagon",
    "parallelogramm",
    "pentagon"
  ];
  availableSymbols.sort(
    (a, b) => (symbolUses.get(a) || 0) - (symbolUses.get(b) || 0)
  );
  return availableSymbols[0];
}

type Chart = {
  uid: Uid<"evaluation" | "chart">;
  chartType: ChartType | null;
  data: ChartItem["data"];
  itemConfigs: ChartItem["itemConfigs"];
  chartSettings: ChartSettings;
  dataToggles: ChartItem["dataToggles"];
  isDefaultSimulation?: boolean;
};

/**
 * Make sure the selected simulation rounds are valid for the selected simulation.
 * @param chart 
 * @returns 
 */
export function updateRoundsToSimulations(
  chart: Chart
) {
  const data = chart.data;

  // if all rounds are selected, no need to adjust.
  if (chart.dataToggles.all.simulationRound) return;

  const { allRoundOptions } = useReactiveChartData(chart);

  const standardRoundOptions = allRoundOptions.filter(
    ({ index }) => index != VIRTUAL_END_ROUND_INDEX
  );

  if (allRoundOptions.length == 0) {
    data.simulationRound = [];
    chart.dataToggles.all.simulationRound = true;
  } else if (standardRoundOptions.length == 0) {
    // only virtual end round exists, no adjustment needed.
  } else {
    const highestRound = standardRoundOptions[standardRoundOptions.length - 1];
    data.simulationRound.forEach((roundIndex, i) => {
      if (roundIndex > highestRound.index)
        data.simulationRound[i] = highestRound.index;
    });
  }
}

/**
 * Add a simulation to the chart config.
 * @param chart 
 * @param simulationId 
 * @returns 
 */
export function addSimulation(chart: Chart, simulationId: number) {
  const chartData = chart.data;
  const selectedSimulationIds = chartData.simulation;

  if (simulationId == -1) {
    // The number -1 is used to add the current open simulation.
  } else {
    // Make sure the simulation exists.
    const simulation = useSimulationStore().items[simulationId];
    if (!simulation) {
      // If it does not exist, remove any settings belonging to it from the config.
      if (simulationId in chart.itemConfigs.simulation) {
        console.log("remove nonexistent simulation", simulationId);
        delete chart.itemConfigs.simulation[simulationId];
      }
      return;
    }
  }

  console.log("add simulation", simulationId);

  if (
    chart.isDefaultSimulation &&
    useSimulationViewStore().simulation == null
  ) {
    useSimulationViewStore().loadSimulation({ simulationId });
  } else {
    selectedSimulationIds.push(simulationId);
  }

  chart.data.selectedSimulationId = simulationId;

  addSimulationSymbol(chart.itemConfigs.simulation, simulationId);

  updateRoundsToSimulations(chart);
}

/**
 * Remove simulations from the chart data.
 * @param chart evaluation or chart
 * @param simulationIds IDs of simulations to remove
 */
export function removeSimulations(chart: Chart, simulationIds: number[]) {
  const chartData = chart.data;
  if (chart.dataToggles.all.simulation) {
    chart.dataToggles.all.simulation = false;
    chartData.simulation = useSimulationStore().order.slice();
  }
  const ids = chartData.simulation;

  for (const simulationId of simulationIds) {
    const index = ids.indexOf(simulationId);
    if (index < 0) continue;
    ids.splice(index, 1);
  }

  updateRoundsToSimulations(chart);
}
